import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Firebase imports (tree-shakeable)
import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, signInWithCredential, GoogleAuthProvider, Auth, signOut } from 'firebase/auth';

// Tipagem para Google Identity Services (GSI)
declare const google: any;

@Injectable({ providedIn: 'root' })
export class GoogleAuthService {

  private gsiLoaded = false;
  private firebaseApp: FirebaseApp | null = null;
  private firebaseAuth: Auth | null = null;

  constructor() {}

  /**
   * Carrega o script Google Identity Services dinamicamente
   */
  loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.gsiLoaded && typeof google !== 'undefined') {
        resolve();
        return;
      }

      const existingScript = document.getElementById('google-gsi-script');
      if (existingScript) {
        // Script ja esta no DOM, aguardar carregamento
        existingScript.addEventListener('load', () => {
          this.gsiLoaded = true;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          reject(new Error('Falha ao carregar Google Identity Services'));
        });
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.gsiLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Falha ao carregar Google Identity Services'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Inicializa Firebase App (se ainda nao inicializado)
   */
  private initFirebase(): void {
    if (this.firebaseApp) return;

    const apps = getApps();
    if (apps.length > 0) {
      this.firebaseApp = apps[0];
    } else {
      this.firebaseApp = initializeApp(environment.firebase);
    }
    this.firebaseAuth = getAuth(this.firebaseApp);
  }

  /**
   * Fluxo completo de autenticacao Google:
   * 1. Carrega GSI script
   * 2. Abre popup Google para selecao de conta
   * 3. Recebe credential (Google ID Token)
   * 4. Usa GoogleAuthProvider.credential() do Firebase
   * 5. signInWithCredential() no Firebase Auth
   * 6. Retorna Firebase ID Token + dados do usuario
   */
  signIn(): Observable<{ idToken: string; email: string; nome: string }> {
    return from(this.loadGoogleScript()).pipe(
      switchMap(() => {
        this.initFirebase();

        return new Observable<string>((observer) => {
          google.accounts.id.initialize({
            client_id: environment.google.clientId,
            callback: (response: any) => {
              if (response.credential) {
                observer.next(response.credential);
                observer.complete();
              } else {
                observer.error(new Error('POPUP_CANCELLED'));
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          // Tenta One Tap primeiro
          google.accounts.id.prompt((notification: any) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              // Se One Tap nao funcionar, usa popup manual via botao renderizado
              // Fallback: renderizar botao oculto e clicar programaticamente
              this.fallbackWithPopup(observer);
            }
          });
        });
      }),
      switchMap((googleCredential: string) => {
        // Converter Google credential para Firebase credential
        const credential = GoogleAuthProvider.credential(googleCredential);
        return from(signInWithCredential(this.firebaseAuth!, credential));
      }),
      switchMap((userCredential) => {
        const user = userCredential.user;
        return from(user.getIdToken()).pipe(
          map((firebaseIdToken) => ({
            idToken: firebaseIdToken,
            email: user.email || '',
            nome: user.displayName || '',
          }))
        );
      })
    );
  }

  /**
   * Fallback: renderizar botao oculto do Google e clicar nele
   */
  private fallbackWithPopup(observer: any): void {
    // Criar container oculto para o botao Google
    let container = document.getElementById('google-signin-fallback');
    if (!container) {
      container = document.createElement('div');
      container.id = 'google-signin-fallback';
      container.style.position = 'fixed';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      document.body.appendChild(container);
    }

    google.accounts.id.renderButton(container, {
      type: 'standard',
      size: 'large',
    });

    // Clicar no botao renderizado para abrir popup
    setTimeout(() => {
      const btn = container?.querySelector('[role="button"]') as HTMLElement;
      if (btn) {
        btn.click();
      } else {
        observer.error(new Error('POPUP_BLOCKED'));
      }
    }, 100);
  }

  /**
   * Limpa estado do Firebase Auth apos uso
   */
  async signOutFirebase(): Promise<void> {
    if (this.firebaseAuth) {
      await signOut(this.firebaseAuth);
    }
  }
}
